import { streamSse } from '@recipes/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useFeature } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { authStore } from '@/lib/auth/auth-store';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { queryKeys } from '@/lib/query-keys';

/** ADMIN and SUPPORT may open the support inbox (docs/partial/email-and-support-spec.md D9). */
export function useIsSupportStaff(): boolean {
  const role = useAuth().user?.role;
  return role === 'ADMIN' || role === 'SUPPORT';
}

const POLL_MS = 60_000;
const RETRY_MS = 30_000;

/**
 * Conversations that wait for an answer – the badge. Polled slowly; while the stream is open,
 * new mail and status changes refresh the badge and every open support list at once.
 */
export function useSupportUnread(): number {
  const staff = useIsSupportStaff();
  const enabled = useFeature('support_inbox') && staff;
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const unread = useQuery({
    queryKey: queryKeys.admin.supportUnread,
    queryFn: () => api.admin.support.unread(),
    enabled,
    refetchInterval: POLL_MS,
  });

  useEffect(() => {
    if (!enabled || !online) return;
    const controller = new AbortController();
    let retry: ReturnType<typeof setTimeout> | undefined;
    const connect = async () => {
      try {
        const stream = streamSse<{ conversationId?: string; unreadCount?: number }>(
          api.admin.support.eventsUrl(),
          {
            getAccessToken: () => authStore.getState().accessToken,
            signal: controller.signal,
          },
        );
        for await (const event of stream) {
          if (typeof event.unreadCount === 'number')
            queryClient.setQueryData(queryKeys.admin.supportUnread, {
              unreadCount: event.unreadCount,
            });
          if (event.conversationId)
            void queryClient.invalidateQueries({ queryKey: ['admin', 'support'] });
        }
      } catch {
        // the slow poll keeps the badge current
      }
      if (!controller.signal.aborted) retry = setTimeout(() => void connect(), RETRY_MS);
    };
    void connect();
    return () => {
      controller.abort();
      clearTimeout(retry);
    };
  }, [enabled, online, queryClient]);

  return enabled ? (unread.data?.unreadCount ?? 0) : 0;
}
